import {
  BarChart,
  ChevronDown,
  DollarSign,
  Eye,
  Linkedin,
  MousePointer,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { linkedinGetAds } from 'sprites-types/workflow/tools/linkedinGetAds'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function AdItem({ ad, className }: { ad: any; className?: string }) {
  const name = ad.name || ad.title || ad.campaignName || 'Ad Campaign'
  const status = ad.status || ad.state || 'active'
  const impressions = ad.impressions || ad.metrics?.impressions || 0
  const clicks = ad.clicks || ad.metrics?.clicks || 0
  const spend = ad.spend || ad.cost || ad.metrics?.cost || 0
  const ctr = ad.ctr || ad.metrics?.ctr || (clicks / impressions) * 100 || 0
  const cpc = ad.cpc || ad.metrics?.averageCpc || spend / clicks || 0
  const objective = ad.objective || ad.campaignObjective || ad.type

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'running':
        return 'bg-green-100 text-green-700'
      case 'paused':
        return 'bg-yellow-100 text-yellow-700'
      case 'ended':
      case 'completed':
        return 'bg-gray-100 text-gray-700'
      default:
        return 'bg-blue-100 text-blue-700'
    }
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-blue-600" />
          <div className="flex-1 text-sm font-semibold text-gray-900">{name}</div>
          <div className={`rounded-full px-2 py-1 text-xs ${getStatusColor(status)}`}>{status}</div>
        </div>

        {objective && <div className="mb-2 text-xs text-gray-600">Objective: {objective}</div>}

        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="flex items-center gap-2">
            <Eye className="h-3 w-3 text-gray-500" />
            <div>
              <div className="font-medium text-gray-900">{formatNumber(impressions)}</div>
              <div className="text-gray-600">Impressions</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <MousePointer className="h-3 w-3 text-gray-500" />
            <div>
              <div className="font-medium text-gray-900">{formatNumber(clicks)}</div>
              <div className="text-gray-600">Clicks</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <DollarSign className="h-3 w-3 text-gray-500" />
            <div>
              <div className="font-medium text-gray-900">{formatCurrency(spend)}</div>
              <div className="text-gray-600">Spend</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <BarChart className="h-3 w-3 text-gray-500" />
            <div>
              <div className="font-medium text-gray-900">{ctr.toFixed(2)}%</div>
              <div className="text-gray-600">CTR</div>
            </div>
          </div>
        </div>

        {clicks > 0 && cpc > 0 && (
          <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="text-xs text-gray-600">
              Average CPC: <span className="font-medium">{formatCurrency(cpc)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function LinkedinGetAdsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(linkedinGetAds, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const ads = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.ads ||
      (result as any)?.campaigns ||
      (result ? [result] : [])
  const totalImpressions = ads.reduce(
    (sum: number, ad: any) => sum + (ad.impressions || ad.metrics?.impressions || 0),
    0
  )
  const totalSpend = ads.reduce(
    (sum: number, ad: any) => sum + (ad.spend || ad.cost || ad.metrics?.cost || 0),
    0
  )

  if (ads.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <TrendingUp className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No LinkedIn ads found</div>
        </div>
      </div>
    )
  }

  if (ads.length === 1) {
    return <AdItem ad={ads[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Linkedin className="h-5 w-5 text-blue-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">LinkedIn Ads</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {ads.length} campaigns • {totalImpressions.toLocaleString()} impressions • $
                {totalSpend.toFixed(2)} spent
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {ads.map((ad: any, index: number) => (
            <AdItem key={ad.id || ad.name || index} ad={ad} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadLinkedinGetAds(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function linkedinGetAdsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(linkedinGetAds, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const ads = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.ads ||
      (result as any)?.campaigns ||
      (result ? [result] : [])

  if (ads.length === 0) {
    return `## LinkedIn Ads

No ads found.

---
`
  }

  const adsMarkdown = ads
    .map((ad: any) => {
      const name = ad.name || ad.title || ad.campaignName || 'Ad Campaign'
      const status = ad.status || ad.state || 'active'
      const impressions = ad.impressions || ad.metrics?.impressions || 0
      const clicks = ad.clicks || ad.metrics?.clicks || 0
      const spend = ad.spend || ad.cost || ad.metrics?.cost || 0
      const ctr = ad.ctr || ad.metrics?.ctr || (clicks / impressions) * 100 || 0
      const objective = ad.objective || ad.campaignObjective || ad.type

      return `### ${name}

**Status:** ${status}
${objective ? `**Objective:** ${objective}` : ''}
**Impressions:** ${impressions.toLocaleString()}
**Clicks:** ${clicks.toLocaleString()}
**Spend:** $${spend.toFixed(2)}
**CTR:** ${ctr.toFixed(2)}%
`
    })
    .join('\n---\n\n')

  return `## LinkedIn Ads (${ads.length} campaigns)

${adsMarkdown}

---
`
}

export const LinkedinGetAdsDisplay = createWorkflowToolComponent(
  LinkedinGetAdsDisplayComponent,
  downloadLinkedinGetAds,
  linkedinGetAdsToMarkdown
)
